/* eslint-disable @typescript-eslint/ban-ts-comment */
import React, { useEffect, useState } from 'react';
import {
  useSearchParams,
  To,
  NavigateOptions,
  useNavigate,
} from 'react-router-dom';
import { useGetProducts, UseGetProductsReturn } from 'hooks/magentoHooks';
import {
  ProductInterface,
  ProductFieldsFragment,
  CartItemInput,
  useAddProductsToCartMutation,
} from 'graphql/generated/magentoApi';
import { useCartContext } from 'CartContext';
import ReactGA from 'react-ga4';
import { DataView } from 'primereact/dataview';
import { Button } from 'primereact/button';
import { Sidebar } from 'primereact/sidebar';
import { Menu } from 'primereact/menu';
import { SliderValueType } from 'primereact/slider';

import FocusLock from 'react-focus-lock';
import Spinner from 'components/Spinner';
import { ProductFilter } from 'components/product-filter/ProductFilter';
import { CategoriesBreadCrumb } from 'components/categories-breadcrumb/CategoriesBreadCrumb';
import ProductCard from '../../pages/ProductCard';

import './CollectionProductList.scss';

type ProductCollectionArrayProps = {
  products: ProductFieldsFragment[];
  urlCollectionName: string;
};

// *function that loops through to return any product with the collectionURL word found in a tag:
const productCollectionArray = ({
  products,
  urlCollectionName,
}: ProductCollectionArrayProps) => {
  const productArray: ProductFieldsFragment[] = [];

  for (let i = 0; i < products.length; i++) {
    if (products[i] && products[i].categories) {
      // @ts-ignore
      for (let z = 0; z < products[i].categories?.length || 0; z++) {
        // @ts-ignore
        const productTag: string | null = products[i]?.categories[z].name
          .toLowerCase()
          .replace(/\s/g, '')
          .replace(/-/g, '')
          .replace(/&/g, '')
          .replace(/,/g, '');

        //* check each product tag to see if it's equal to the collection/collectionURL:
        if (
          String(productTag) ===
          String(
            urlCollectionName
              .replace(/\s/g, '')
              .replace(/-/g, '')
              .replace(/&/g, ''),
          )
        ) {
          productArray.push(products[i]);
        }
      }
    }
  }
  const removeDuplicateProducts = [...new Set(productArray)];

  return removeDuplicateProducts;
};

interface companyVendors {
  name?: string;
}

export default ({
  collectionURL,
  shouldShowAside,
  setLoading,
  setVisibleRight,
  isMobile,
  menuModel,
  setMenuModel,
}) => {
  const { fetching, products: initialProducts }: UseGetProductsReturn =
    useGetProducts();
  const products = productCollectionArray({
    products: initialProducts,
    urlCollectionName: collectionURL,
  });

  const navigateBase = useNavigate();
  const [, addProducts] = useAddProductsToCartMutation();
  const [searchParams] = useSearchParams();
  const openExternal = searchParams.get('open-external') === 'true';
  const [productSku, setCurrentProductSku] = useState<string>('');
  const { cartId } = useCartContext();
  // set default path:
  const [categoryPaths, setCategoryPaths] = useState([
    // collectionURL.includes('health') || collectionURL.includes('vitamins')
    //   ? 'shopping/health'
    //   : 'shopping/beauty',
    // if (collectionURL.includes('health')) {
    //     return 'shopping/health';
    // } else if (collectionURL.includes('vitamins')) {
    //     return 'shopping/beauty'
    // }
    collectionURL.includes('health') || collectionURL.includes('vitamins')
      ? 'shopping/health'
      : 'shopping/beauty',
  ]);
  const [loadingProduct, setLoadingProduct] = useState(false);
  const [visible, setVisible] = useState<boolean>(false);
  const [loadingCategories, setLoadingCategories] = useState<boolean>(false);
  const [selectedVendors, setSelectedVendors] =
    useState<Array<companyVendors> | null>(null);
  const [collectionProducts, setCollectionProducts] = useState<
    ProductFieldsFragment[]
  >(products || []);

  const itemTemplate = (product: ProductInterface) => (
    <ProductCard
      product={product}
      onClickAddBtn={onClickAddBtn}
      loading={loadingProduct && product.sku === productSku}
    />
  );
  // * do we need this?
  const navigate = (to: To, options?: NavigateOptions) => {
    if (openExternal) {
      window.open(to as string, '_blank');
    } else {
      navigateBase(to, options);
    }
  };

  const onClickAddBtn = (sku: string) => {
    if (openExternal) {
      navigate(`/store/cart?add-sku=${sku}`);
      return;
    }
    setLoadingProduct(true);
    setVisibleRight(true);
    setCurrentProductSku(sku);
    addProducts({
      cartId,
      cartItems: [
        {
          sku,
          quantity: 1,
        },
      ] as CartItemInput[],
    });
    ReactGA.event({
      category: 'ecommerce',
      action: 'add_to_cart',
      label: `Product Card Action ${sku}`,
    });
  };

  const maxProductPrice =
    products.sort(
      (a, b) =>
        (b.price_range.maximum_price?.final_price.value || 0) -
        (a.price_range.maximum_price?.final_price.value || 0),
    )[0]?.price_range.maximum_price?.final_price.value || 0;

  const [priceRange, setPriceRange] = useState<SliderValueType>([
    0,
    maxProductPrice,
  ]);

  useEffect(() => {
    let filterProducts: Array<ProductFieldsFragment> = [...products];

    if (!fetching) {
      filterProducts = filterProducts.filter(
        item =>
          (item?.price_range.maximum_price?.final_price?.value || 0) >=
            priceRange[0] &&
          (item?.price_range.maximum_price?.final_price?.value || 0) <=
            priceRange[1],
      );

      if (selectedVendors) {
        filterProducts = filterProducts.filter(item =>
          selectedVendors.some(
            vendorItem => item?.vendor_company === vendorItem.name,
          ),
        );
      }
      if (
        JSON.stringify(filterProducts) !== JSON.stringify(collectionProducts)
      ) {
        setCollectionProducts(filterProducts);
      }
      setLoading(true);
    }
  }, [
    fetching,
    selectedVendors,
    priceRange,
    products,
    collectionProducts,
    setLoading,
  ]);

  return (
    <>
      <div
        className="categories grid"
        style={{ maxWidth: '1140px', margin: '0 auto' }}
      >
        {shouldShowAside && (
          <>
            {isMobile ? (
              <div className="mb-4">
                <Sidebar
                  visible={visible}
                  onHide={() => setVisible(false)}
                  className="sidebar-filter"
                >
                  <FocusLock>
                    <aside className="col-10 sm:col-12 lg:col-3 px-1 sm:px-3 lg:px-2">
                      {!loadingCategories && categoryPaths ? (
                        <>
                          <h3 className="uppercase mb-5">Product Filters</h3>
                          {typeof menuModel[0].items === 'object' &&
                          menuModel[0].items.length !== 0 ? (
                            <Menu
                              model={menuModel}
                              className="w-full shop-categories p-card p-0 mb-3"
                            />
                          ) : (
                            <CategoriesBreadCrumb
                              setLoadingCategories={setLoadingCategories}
                              setMenuModel={setMenuModel}
                              setCategoryPaths={setCategoryPaths}
                              categoryPaths={categoryPaths}
                            />
                          )}
                        </>
                      ) : (
                        <Spinner />
                      )}
                      {collectionProducts && (
                        <ProductFilter
                          setVisible={setVisible}
                          setSelectedVendors={setSelectedVendors}
                          selectedVendors={selectedVendors}
                          shouldShowAside={shouldShowAside}
                          isMobile={isMobile}
                          products={products}
                          priceRange={priceRange}
                          setPriceRange={setPriceRange}
                          maxProductPrice={maxProductPrice}
                        />
                      )}
                    </aside>
                  </FocusLock>
                </Sidebar>
                <Button
                  icon="pi pi-filter"
                  label="Filter Products"
                  className="shop-button green solid ml-2 my-0 py-2 px-2 text-xs flex align-items-center flex-row"
                  onClick={() => setVisible(true)}
                />
              </div>
            ) : (
              <aside className="col-12 lg:col-3 px-3 lg:px-2 cat-aside">
                {!loadingCategories && categoryPaths ? (
                  <>
                    {typeof menuModel[0].items === 'object' &&
                    menuModel[0].items.length !== 0 ? (
                      <Menu
                        model={menuModel}
                        className="w-full shop-categories p-card p-0 mb-3"
                      />
                    ) : (
                      <CategoriesBreadCrumb
                        setLoadingCategories={setLoadingCategories}
                        setMenuModel={setMenuModel}
                        setCategoryPaths={setCategoryPaths}
                        categoryPaths={categoryPaths}
                        showHome
                      />
                    )}
                    <ProductFilter
                      setVisible={setVisible}
                      setSelectedVendors={setSelectedVendors}
                      selectedVendors={selectedVendors}
                      shouldShowAside={shouldShowAside}
                      isMobile={isMobile}
                      products={products}
                      priceRange={priceRange}
                      setPriceRange={setPriceRange}
                      maxProductPrice={maxProductPrice}
                    />
                  </>
                ) : (
                  <Spinner />
                )}
              </aside>
            )}
          </>
        )}
        {collectionProducts.length >= 1 && (
          <DataView
            rows={9}
            value={collectionProducts}
            paginator
            itemTemplate={itemTemplate}
            layout="grid"
            className={`max-w-full col-12 scalein p-0${
              shouldShowAside ? ' lg:col-9' : ''
            }`}
          />
        )}
      </div>
    </>
  );
};
